<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue';
import CalculationProcess from '@/presentation/components/calculation-process/CalculationProcess.vue';
import CopilotBox from '@/presentation/components/copilot-box/CopilotBox.vue';
import { useI18n } from 'vue-i18n';
import { useAiDisclosureResult } from '@/composables/use-ai-disclosure-result';

const MOCK_PROCESS_TIME = 1000;
const LAST_PROCESS_IDX = 3;
const { t } = useI18n();

const { aiDisclosureResultData } = useAiDisclosureResult();

const processLabels = ref([
    t('aiDisclosure.loadingText1'),
    t('aiDisclosure.loadingText2'),
    t('aiDisclosure.loadingText3'),
    t('aiDisclosure.loadingText4')
]);
const processIdx = ref(0);
const hasTechMeans = ref(false);

const emits = defineEmits<{
    (e: 'loaded'): void;
}>();

let timer;
function startLoading() {
    timer = setInterval(() => {
        if (processIdx.value === 1) {
            if (hasTechMeans.value) {
                processIdx.value = 2;
            } else {
                clearInterval(timer);
            }
            return;
        }

        processIdx.value += 1;
        if (processIdx.value > LAST_PROCESS_IDX) {
            clearInterval(timer);
            emits('loaded');
        }
    }, MOCK_PROCESS_TIME);
}

watch(() => aiDisclosureResultData.value.settings.techMeans, () => {
    hasTechMeans.value = !!aiDisclosureResultData.value.settings.techMeans;
    if (hasTechMeans.value) {
        processIdx.value = 2;
        startLoading();
    }
}, {
    deep: true,
});

startLoading();

function getProcessStatus(idx: number) {
    if (processIdx.value === idx) {
        return 'executing';
    }
    if (processIdx.value > idx) {
        return 'success';
    }
    return 'unexecuted';
}

onUnmounted(() => {
    if (timer) {
        clearInterval(timer);
    }
});
</script>

<template>
    <div class="disclosure-side-load mx-4 mt-3">
        <CopilotBox class="w-[288px]">
            <div class="line-clamp-1 text-sm font-semibold text-black">
                {{ t('aiDisclosure.sideLoadingTitle') }}
            </div>

            <CalculationProcess
                v-for="(item, idx) in processLabels"
                :key="item"
                class="mt-4"
                :label="item"
                :status="getProcessStatus(idx)"
            />
        </CopilotBox>
    </div>
</template>

<style lang="scss"></style>
